import { createContext, useContext, ReactNode, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { GetProjectsResponse, projectApi } from "@/services/projectService";
import { unifiedInvitationService } from "@/services/unifiedInvitationService";
import { GetProjectsResponseDto } from "@/dto/get-projects-response.dto";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { ProjectStatus } from "@/enums/project.enum";

// Simple interface for member invitation
interface InviteMemberData {
  email: string;
  role: string;
}

interface ProjectContextType {
  projects: GetProjectsResponseDto[];
  isLoading: boolean;
  removingUser: boolean;
  deletingInvite: boolean;
  resendingInvite: boolean;
  isError: boolean;
  error: unknown;
  getProjectById: (id: string) => GetProjectsResponseDto | undefined;
  addMemberToProject: (
    projectId: string,
    dto: InviteMemberData
  ) => Promise<void>;
  removeMemberFromProject: (projectId: string, userId: string) => Promise<void>;
  addComment: (
    projectId: string, 
    content: string, 
    richContent?: string,
    attachments?: File[],
    mentions?: string[],
    parentComment?: string,
    parentActivityId?: string
  ) => Promise<void>;  editComment: (
    projectId: string,
    commentId: string,
    content: string,
    richContent?: string,
    attachments?: File[],
    replaceAttachments?: boolean
  ) => Promise<void>;
  removeComment: (projectId: string, commentId: string) => Promise<void>;
  updateStatus: (projectId: string, status: ProjectStatus) => Promise<void>;
  deleteProjectInvite: (inviteId: string, projectId: string) => Promise<void>;
  resendProjectInvite: (inviteId: string, projectId: string) => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export function ProjectProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();

  // Fetch projects with React Query
  const {
    data: projectsData,
    isFetching: isLoading,
    isError,
    error,
  } = useQuery<GetProjectsResponse>({
    queryKey: ["projects"],
    queryFn: () => {
      return projectApi.getProjects();
    },
    initialData: { projects: [] },
    enabled: !!isAuthenticated,
    retry: 0,
  });

  // Prefetch recent project details for faster navigation
  useEffect(() => {
    if (projectsData?.projects?.length > 0) {
      // Sort by updatedAt descending to get most recent projects
      const sortedProjects = [...projectsData.projects].sort((a, b) => {
        const dateA = new Date(a.updatedAt || 0).getTime();
        const dateB = new Date(b.updatedAt || 0).getTime();
        return dateB - dateA;
      });

      // 1. Identify high priority projects (active ones first)
      const activeProjects = sortedProjects.filter(
        (p) =>
          p.status !== ProjectStatus.COMPLETED &&
          p.status !== ProjectStatus.REJECTED
      );

      const inactiveProjects = sortedProjects.filter(
        (p) =>
          p.status === ProjectStatus.COMPLETED ||
          p.status === ProjectStatus.REJECTED
      );

      // Recombine: active first, then inactive
      const allPrioritized = [...activeProjects, ...inactiveProjects];

      // 2. Immediate Batch: Top 10
      const immediateBatch = allPrioritized.slice(0, 10);
      
      immediateBatch.forEach((project) => {
        queryClient.prefetchQuery({
          queryKey: ["projects", project._id],
          queryFn: () => projectApi.getProject(project._id),
          staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
        });
      });

      // 3. Remaining Batch: Fetch gradually
      const remainingBatch = allPrioritized.slice(10);
      
      if (remainingBatch.length > 0) {
        const fetchRemaining = async () => {
          // Process in chunks to avoid network congestion
          const CHUNK_SIZE = 5;
          for (let i = 0; i < remainingBatch.length; i += CHUNK_SIZE) {
            const chunk = remainingBatch.slice(i, i + CHUNK_SIZE);
            
            // Execute chunk in parallel
            await Promise.all(
              chunk.map((project) =>
                queryClient.prefetchQuery({
                  queryKey: ["projects", project._id],
                  queryFn: () => projectApi.getProject(project._id),
                  staleTime: 1000 * 60 * 5,
                })
              )
            );
            
            // Small delay between chunks
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        };

        // Start fetching remaining projects after a short delay
        setTimeout(() => {
          fetchRemaining();
        }, 2000);
      }
    }
  }, [projectsData, queryClient]);

  const addMemberMutation = useMutation({
    mutationFn: ({
      projectId,
      dto,
    }: {
      projectId: string;
      dto: InviteMemberData;
    }) => unifiedInvitationService.inviteUserToProject(projectId, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: ({
      projectId,
      userId,
    }: {
      projectId: string;
      userId: string;
    }) => projectApi.removeMemeberFromProject(projectId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });

  const deleterProjectInviteMutation = useMutation({
    mutationFn: ({ inviteId }: { inviteId: string; projectId: string }) =>
      unifiedInvitationService.deleteProjectInvitation(inviteId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["projects", variables.projectId, "invites"],
      });
    },
  });

  const resendProjectInviteMutation = useMutation({
    mutationFn: ({ inviteId }: { inviteId: string; projectId: string }) =>
      unifiedInvitationService.resendProjectInvitation(inviteId),
    onSuccess: (_data, variables) => {
      toast({
        title: "Invitation resent",
        description: "The invitation has been successfully resent.",
      });
      queryClient.invalidateQueries({
        queryKey: ["projects", variables.projectId, "invites"],
      });
    },
    onError: (error: unknown) => {
      const errorMessage = error && typeof error === 'object' && 'response' in error &&
        error.response && typeof error.response === 'object' && 'data' in error.response &&
        error.response.data && typeof error.response.data === 'object' && 'error' in error.response.data
        ? String((error.response.data as { error: string }).error)
        : "Failed to resend invitation. Please try again.";
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const addCommentMutation = useMutation({
    mutationFn: ({
      projectId,
      content,
      richContent,
      attachments,
      mentions,
      parentComment,
      parentActivityId,
    }: {
      projectId: string;
      content: string;
      richContent?: string;
      attachments?: File[];
      mentions?: string[];
      parentComment?: string;
      parentActivityId?: string;
    }) => projectApi.addComment(projectId, content, richContent, attachments, mentions, parentComment, parentActivityId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects", variables.projectId] });
    },
  });
  const removeCommentMutation = useMutation({
    mutationFn: ({
      projectId,
      commentId,
    }: {
      projectId: string;
      commentId: string;
    }) => projectApi.deleteComment(projectId, commentId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects", variables.projectId] });
    },
  });
  const updateProjectStatusMutation = useMutation({
    mutationFn: ({
      projectId,
      status,
    }: {
      projectId: string;
      status: ProjectStatus;
    }) => projectApi.updateProjectStatus(projectId, status),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects", variables.projectId] });
    },
  });  const editCommentMutation = useMutation({
    mutationFn: ({
      projectId,
      commentId,
      content,
      richContent,
      attachments,
      replaceAttachments,
    }: {
      projectId: string;
      commentId: string;
      content: string;
      richContent?: string;
      attachments?: File[];
      replaceAttachments?: boolean;
    }) => projectApi.editComment(projectId, commentId, content, richContent, attachments, replaceAttachments),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["projects", variables.projectId] });
    },
  });

  const addMemberToProject = async (
    projectId: string,
    dto: InviteMemberData
  ) => {
    await addMemberMutation.mutateAsync({ projectId, dto });
  };

  const removeMemberFromProject = async (projectId: string, userId: string) => {
    await removeMemberMutation.mutateAsync({ projectId, userId });
  };

  const deleteProjectInvite = async (inviteId: string, projectId: string) => {
    await deleterProjectInviteMutation.mutateAsync({ inviteId, projectId });
  };

  const resendProjectInvite = async (inviteId: string, projectId: string) => {
    await resendProjectInviteMutation.mutateAsync({ inviteId, projectId });
  };

  const addComment = async (
    projectId: string, 
    content: string, 
    richContent?: string,
    attachments?: File[],
    mentions?: string[],
    parentComment?: string,
    parentActivityId?: string
  ) => {
    await addCommentMutation.mutateAsync({ 
      projectId, 
      content, 
      richContent, 
      attachments, 
      mentions, 
      parentComment,
      parentActivityId
    });
  };
  const editComment = async (
    projectId: string,
    commentId: string,
    content: string,
    richContent?: string,
    attachments?: File[],
    replaceAttachments?: boolean
  ) => {
    await editCommentMutation.mutateAsync({ 
      projectId, 
      commentId, 
      content, 
      richContent,
      attachments,
      replaceAttachments
    });
  };

  const removeComment = async (projectId: string, commentId: string) => {
    await removeCommentMutation.mutateAsync({ projectId, commentId });
  };

  const updateStatus = async (projectId: string, status: ProjectStatus) => {
    await updateProjectStatusMutation.mutateAsync({ projectId, status });
  };

  const getProjectById = (id: string) => {
    return projectsData?.projects.find((proj) => proj._id === id);
  };

  return (
    <ProjectContext.Provider
      value={{
        projects: projectsData?.projects || [],
        isLoading,
        isError,
        error,
        getProjectById,
        addMemberToProject,
        removeMemberFromProject,        addComment,
        editComment,
        removeComment,
        updateStatus,
        deleteProjectInvite,
        resendProjectInvite,
        removingUser: removeMemberMutation.isPending,
        deletingInvite: deleterProjectInviteMutation.isPending,
        resendingInvite: resendProjectInviteMutation.isPending,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useProjects = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error("useProjects must be used within a ProjectProvider");
  }
  return context;
};
